import { getApiUrl } from "@/utils/api";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, KeyRound, UserCircle as UserIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = getApiUrl();

export default function AdminAccountTab() {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  // Email form
  const [newEmail, setNewEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  // Username form
  const [newUsername, setNewUsername] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);

  // Password form
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/auth/me`, { withCredentials: true });
        if (!cancelled) {
          setMe(res.data);
          setNewUsername(res.data.username || "");
          setNewEmail(res.data.email && res.data.email !== "admin@local" ? res.data.email : "");
        }
      } catch (err) {
        toast.error("Failed to load admin account");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveUsername = async () => {
    const trimmed = newUsername.trim();
    if (trimmed.length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    if (trimmed === me?.username) {
      toast.info("Username unchanged");
      return;
    }
    setSavingUsername(true);
    try {
      await axios.post(
        `${API}/admin/auth/change-credentials`,
        { new_username: trimmed },
        { withCredentials: true }
      );
      setMe((p) => ({ ...p, username: trimmed }));
      toast.success("Username updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update username");
    } finally {
      setSavingUsername(false);
    }
  };

  const handleSaveEmail = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (trimmed === (me?.email || "").toLowerCase()) {
      toast.info("Email unchanged");
      return;
    }
    setSavingEmail(true);
    try {
      await axios.post(
        `${API}/admin/auth/change-credentials`,
        { new_email: trimmed },
        { withCredentials: true }
      );
      setMe((p) => ({ ...p, email: trimmed }));
      toast.success("Recovery email updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update email");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSavePassword = async () => {
    if (newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(
        `${API}/admin/auth/change-credentials`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password updated — use it on your next login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-border p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-account-tab">
      {/* Username */}
      <div className="bg-white border border-border p-6 space-y-4">
        <div className="flex items-center gap-3 border-b border-border pb-3">
          <UserIcon className="h-5 w-5 text-primary" />
          <h3 className="font-serif text-lg font-medium">Username</h3>
        </div>
        <div className="space-y-2 max-w-md">
          <Label htmlFor="admin-account-username">Master admin username</Label>
          <Input
            id="admin-account-username"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            placeholder="admin"
            className="rounded-none"
            data-testid="admin-account-username-input"
          />
          <p className="text-xs text-muted-foreground">
            Used to sign in at <code className="text-xs">/admin/login</code>.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSaveUsername}
            disabled={savingUsername || !newUsername.trim() || newUsername.trim() === me?.username}
            className="rounded-none"
            data-testid="admin-account-save-username-btn"
          >
            {savingUsername ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Username
          </Button>
        </div>
      </div>

      {/* Recovery email */}
      <div className="bg-white border border-border p-6 space-y-4">
        <div className="flex items-center gap-3 border-b border-border pb-3">
          <UserIcon className="h-5 w-5 text-primary" />
          <h3 className="font-serif text-lg font-medium">Recovery email</h3>
        </div>
        <div className="space-y-2 max-w-md">
          <Label htmlFor="admin-account-email">Email address</Label>
          <Input
            id="admin-account-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@example.com"
            className="rounded-none"
            data-testid="admin-account-email-input"
          />
          <p className="text-xs text-muted-foreground">
            Used for "Forgot password" reset links. Make sure you have access to this inbox.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSaveEmail}
            disabled={savingEmail || !newEmail.trim() || newEmail.trim().toLowerCase() === (me?.email || "").toLowerCase()}
            className="rounded-none"
            data-testid="admin-account-save-email-btn"
          >
            {savingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Email
          </Button>
        </div>
      </div>

      {/* Password */}
      <div className="bg-white border border-border p-6 space-y-4">
        <div className="flex items-center gap-3 border-b border-border pb-3">
          <KeyRound className="h-5 w-5 text-primary" />
          <h3 className="font-serif text-lg font-medium">Password</h3>
        </div>
        <div className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="admin-account-new-password">New password</Label>
            <div className="relative">
              <Input
                id="admin-account-new-password"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="rounded-none pr-10"
                autoComplete="new-password"
                data-testid="admin-account-new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-account-confirm-password">Confirm new password</Label>
            <Input
              id="admin-account-confirm-password"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              className="rounded-none"
              autoComplete="new-password"
              data-testid="admin-account-confirm-password-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Minimum 4 characters. You'll keep your current session — the new password applies on next login.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSavePassword}
            disabled={savingPassword || !newPassword || !confirmPassword}
            className="rounded-none"
            data-testid="admin-account-save-password-btn"
          >
            {savingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Password
          </Button>
        </div>
      </div>
    </div>
  );
}
